import { useEffect, useState } from 'react';
import {
    Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material';
import MDButton from '@components/MDButton';
import { CurrencyInput } from '@components/CurrencyInput';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { getCrmColors } from '../../crm-theme';

interface Props {
    open: boolean;
    leadName: string;
    /// Ausente quando o ganho não vem de arrastar o card.
    columnName?: string;
    /// O que foi estimado ao entrar o lead; vem preenchido para quem acertou não precisar digitar.
    estimated?: number | null;
    onCancel: () => void;
    onConfirm: (closedValue: number | null) => void;
}

export const CrmWonValueModal = ({
    open, leadName, columnName, estimated, onCancel, onConfirm,
}: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);

    const [number, setNumber] = useState<number | null>(null);

    useEffect(() => {
        if (!open) return;

        setNumber(estimated ?? null);
    }, [open, estimated]);

    const changed = estimated != null && number !== null && number !== estimated;

    return (
        <Dialog open={open} onClose={onCancel} fullWidth maxWidth="xs">
            <DialogTitle sx={{ pb: 0.5 }}>
                {t('ganho.titulo')}
                <Typography variant="caption" sx={{ display: 'block', color: cc.textSecondary }}>
                    {columnName
                        ? t('ganho.sub', { lead: leadName, etapa: columnName })
                        : leadName}
                </Typography>
            </DialogTitle>

            <DialogContent dividers sx={{ display: 'grid', gap: 1, pt: 2 }}>
                <CurrencyInput
                    autoFocus
                    fullWidth
                    value={number}
                    onChange={setNumber}
                    label={t('ganho.valorFechado')}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            bgcolor: cc.surfaceBg,
                            borderRadius: '8px',
                            color: cc.textPrimary,
                            '& fieldset': { borderColor: cc.border },
                        },
                        '& .MuiInputBase-input': { color: cc.textPrimary },
                        '& .MuiFormLabel-root': { color: cc.textSecondary },
                    }}
                />

                <Typography variant="caption" sx={{ color: cc.textMuted }}>
                    {estimated != null
                        ? t('ganho.estimadoEra', {
                            valor: estimated.toLocaleString('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                            }),
                        })
                        : t('ganho.semEstimativa')}
                </Typography>

                {changed && (
                    <Typography
                        variant="caption"
                        sx={{ color: number! > estimated! ? cc.eventSuccess : cc.eventWarning }}
                    >
                        {t(number! > estimated! ? 'ganho.acimaDoEstimado' : 'ganho.abaixoDoEstimado')}
                    </Typography>
                )}
            </DialogContent>

            <DialogActions>
                <MDButton variant="gradient" color="dark" onClick={onCancel}>
                    {t('general.cancelar')}
                </MDButton>

                {/* Sem valor, o ganho conta pela estimativa, que já vem preenchida no campo. */}
                <MDButton variant="gradient" color="dark" onClick={() => onConfirm(null)}>
                    {t('ganho.naoInformar')}
                </MDButton>

                <MDButton
                    variant="gradient"
                    color="success"
                    onClick={() => onConfirm(number)}
                    disabled={number === null}
                >
                    {t('ganho.confirmar')}
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};
